import { HStack } from '../../../components/primitives';
import type { LeadSource } from '../../../types/leads';
import { getLeadPlatforms } from '../utils/leadPlatforms';
import { SourceBadge } from './SourceBadge';

interface LeadSourceBadgesProps {
  readonly lead: {
    source: LeadSource;
    sourceUrl?: string | null;
    tags?: string[] | null;
    contacts?: Array<{ sourceUrl?: string | null }> | null;
  };
  readonly showLabel?: boolean;
}

/**
 * Every network this lead has been enriched on, not just the one that happened to
 * win `Lead.sourceUrl`.
 *
 * The three call sites — lead detail, pipeline card, lead list — each rendered a
 * single `SourceBadge` from `lead.source`, so a lead enriched on LinkedIn and then
 * on Instagram claimed to be LinkedIn only. `getLeadPlatforms` explains which
 * signals are read and why the pipeline card has to rely on tags.
 *
 * Falls back to `lead.source` when no network can be identified, which keeps a
 * manual, imported or email lead showing exactly what it showed before.
 */
export function LeadSourceBadges({ lead, showLabel = true }: LeadSourceBadgesProps) {
  const platforms = getLeadPlatforms(lead);

  if (platforms.length === 0) {
    return <SourceBadge source={lead.source} showLabel={showLabel} />;
  }

  return (
    <HStack spacing="xs" align="center" style={{ flexWrap: 'wrap' }}>
      {platforms.map((platform) => (
        <SourceBadge key={platform} source={platform as LeadSource} showLabel={showLabel} />
      ))}
    </HStack>
  );
}
